import type {
  AriaAttributes,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import React, { forwardRef, useEffect, useState } from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'
import { ActionTypes } from '../Menu/Actions'
import { useMenu } from '../Menu/Menu'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type {
  TextInputColor,
  TextInputSize,
  TextInputVariant,
} from '../TextInput/styles'
import TextInput from '../TextInput/TextInput'
import { Focus } from '../utilities/calculateActiveIndex'
import { Keys } from '../utilities/keyboard'
import { ActionTypes as AutocompleteActionTypes } from './Actions'
import { DisplayModes, useAutocomplete } from './Autocomplete'

export type TextProps = {
  clearable?: boolean
  color: TextInputColor
  dataSet?: Record<string, string | undefined>
  defaultValue?: string
  defaultDisplayValue?: string
  disabled?: boolean
  endAdornment?: typeof SvgIcon | string
  error?: boolean
  helperText?: string
  inputRef?: Ref<HTMLInputElement>
  inMenu?: boolean
  displayRef?: Ref<HTMLInputElement>
  label: string
  name?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  size?: TextInputSize
  startAdornment?: typeof SvgIcon | string
  variant?: TextInputVariant
}

interface TextPassThruProps {
  onBlur: FocusEventHandler<HTMLInputElement>
  onChange: ChangeEventHandler<HTMLInputElement>
  onFocus: FocusEventHandler<HTMLInputElement>
  onKeyDown: KeyboardEventHandler<HTMLInputElement>
}

interface MenuPassThruProps {
  'aria-activedescendant'?: AriaAttributes['aria-activedescendant'] //TODO: do we need this????
  'aria-autocomplete'?: AriaAttributes['aria-autocomplete']
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  id: string
  onClick: MouseEventHandler<HTMLInputElement>
  ref: Ref<HTMLDivElement>
  role: string
}

function Text(
  {
    clearable = false,
    color,
    dataSet,
    defaultValue = '',
    defaultDisplayValue,
    displayRef,
    disabled,
    endAdornment,
    error,
    helperText,
    inputRef,
    inMenu,
    label,
    onBlur,
    onChange,
    onFocus,
    name,
    size,
    startAdornment,
    variant,
  }: TextProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const [blurTarget, setBlurTarget] = useState<HTMLInputElement | null>(null)
  const [state, dispatch] = useMenu()
  const [
    {
      displayInputRef,
      groups,
      label: selectedLabel,
      inputRef: localRef,
      multi,
      select,
      selectingRef,
      value,
    },
    dispatchAutocomplete,
  ] = useAutocomplete()

  const hiddenInputRef = useSyncRefs(inputRef, localRef)
  const buttonRef = useSyncRefs(state.buttonRef, ref)

  useEffect(() => {
    if (localRef.current) {
      localRef.current.value = defaultValue
    }
  }, [defaultValue, localRef])

  useEffect(() => {
    if (!state.show && blurTarget) {
      blurTarget.blur()
    }
  }, [blurTarget, state.show])

  const activeId =
    state?.activeItemIndex !== null && state.items[state.activeItemIndex]?.id
  const activeLabel = activeId && document.getElementById(activeId)?.textContent

  const commonProps: TextPassThruProps = {
    onBlur: (event) => {
      event.preventDefault()

      if (!inMenu) {
        if (state.show) {
          event.target.focus()
        } else {
          if (select && !multi) {
            selectingRef.current = true

            const descriptor = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value',
            ) as PropertyDescriptor
            const inputEvent = new Event('input', { bubbles: true })

            descriptor.set?.call(event.target, selectedLabel)
            event.target.dispatchEvent(inputEvent)

            dispatchAutocomplete({
              type: AutocompleteActionTypes.SelectOption,
              label: selectedLabel,
              value,
            })
          }
        }
      }

      if (!state.show || inMenu) {
        setBlurTarget(null)
        onBlur?.(event)
      }
    },
    onChange: (event) => {
      if (!event.target?.value && !multi) {
        dispatchAutocomplete({ type: AutocompleteActionTypes.ClearSearching })
        dispatch({ type: ActionTypes.CloseMenu })

        return
      }

      if (selectingRef.current) {
        selectingRef.current = false

        if (selectedLabel === event.target?.value) {
          return
        }
      }

      dispatchAutocomplete({ type: AutocompleteActionTypes.SetSearching })

      if (!state.show) {
        dispatch({ type: ActionTypes.OpenMenu })
      }

      onChange?.(event)
    },
    onFocus: (event) => {
      if (
        (!state.show || inMenu) &&
        (event.currentTarget?.value ||
          Object.values(groups).some(
            (group) =>
              group.display !== DisplayModes.WithTerm && group.hasChildren,
          ))
      ) {
        if (!inMenu) {
          event.preventDefault()
          dispatch({ type: ActionTypes.OpenMenu })
        }

        onFocus?.(event)
      }
    },
    onKeyDown: (event) => {
      if (inMenu) {
        event.stopPropagation()
      }

      if (event.repeat) {
        return
      }

      switch (event.key) {
        case Keys.Tab:
          if (state.show && !inMenu) {
            dispatch({ type: ActionTypes.CloseMenu })
          }

          break

        case Keys.Escape:
          if (state.show) {
            if (inMenu) {
              state.itemsRef.current?.focus()
            } else {
              dispatch({ type: ActionTypes.CloseMenu })
            }
          }
          break

        case Keys.Enter:
          if (state.activeItemIndex != null && state.show) {
            const itemId = state.items[state.activeItemIndex].id
            document.getElementById(itemId)?.click()

            if (!multi) {
              dispatch({ type: ActionTypes.SelectItem, id: itemId })
            }
          }
          break

        case Keys.ArrowDown:
          event.preventDefault()
          if (!state.show) {
            if (
              displayInputRef.current?.value ||
              Object.values(groups).some(
                (group) =>
                  group.display !== DisplayModes.WithTerm && group.hasChildren,
              )
            ) {
              dispatch({ type: ActionTypes.OpenMenu })
            }
          } else {
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.Next })
          }
          break

        case Keys.ArrowUp:
          event.preventDefault()
          if (!state.show) {
            dispatch({ type: ActionTypes.OpenMenu })
          } else {
            dispatch({ type: ActionTypes.GoToItem, focus: Focus.Previous })
          }
      }
    },
  }

  const menuProps: MenuPassThruProps = {
    'aria-activedescendant':
      activeLabel && state.show ? activeLabel : undefined,
    'aria-autocomplete': 'list',
    'aria-expanded': state.show ? 'true' : undefined,
    'aria-haspopup': 'listbox',
    'aria-controls': `coconut-menu-${state.id}`,
    id: `coconut-menu-button-${state.id}`,
    role: 'combobox',
    onClick: (event) => {
      if (!state.show && event.currentTarget?.value) {
        event.preventDefault()
        dispatch({ type: ActionTypes.OpenMenu })
      }
    },
    ref: buttonRef,
  }

  useEffect(() => {
    if (inMenu && multi) {
      dispatch({ type: ActionTypes.RegisterMultiSelect })
    } else if (!inMenu) {
      dispatch?.({ type: ActionTypes.RegisterSelect })
    }
  }, [dispatch, inMenu, multi])

  const clearInput: MouseEventHandler<HTMLButtonElement> = () => {
    if (localRef.current) localRef.current.value = ''

    dispatchAutocomplete({ type: AutocompleteActionTypes.Clear })
  }

  return (
    <>
      {select ? (
        <input
          className="hidden"
          defaultValue={defaultValue}
          disabled={disabled}
          name={name}
          ref={hiddenInputRef}
        />
      ) : null}
      <TextInput
        color={color}
        dataSet={dataSet}
        defaultValue={defaultDisplayValue}
        disabled={disabled}
        endAdornment={endAdornment}
        error={error}
        helperText={helperText}
        inputRef={displayRef}
        label={label}
        {...(select ? {} : { name })}
        onClear={clearable && selectedLabel ? clearInput : undefined}
        size={size}
        startAdornment={startAdornment}
        variant={variant}
        {...commonProps}
        {...(inMenu ? {} : menuProps)}
      />
    </>
  )
}

export default forwardRef<HTMLDivElement, PropsWithChildren<TextProps>>(Text)
